<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<!--
  Testi in italiano diretti, senza $t: pagina riservata agli amministratori
  italiani, e ogni lang string in più è una possibile collisione (vedi
  docs/NOTE_SPESE.md).
-->

<template>
  <div class="ns-app">
    <div v-if="status === 'loading'" class="ns-app__center">
      <span class="ns-app__spinner" aria-hidden="true"></span>
      <p>Caricamento…</p>
    </div>

    <div v-else-if="status === 'error'" class="ns-app__center">
      <p>{{ loadError }}</p>
      <button type="button" class="ns-app__retry" @click="fetchProfile">
        Riprova
      </button>
    </div>

    <div v-else-if="!profile.isDirector" class="ns-app__center">
      <p class="ns-app__denied">
        Questa pagina è riservata agli amministratori della società. Se pensi di
        doverla vedere, chiedi all'ufficio del personale.
      </p>
    </div>

    <template v-else>
      <month-view
        v-if="screen === 'month'"
        :period="period"
        :report="report"
        :loading="isLoadingMonth"
        :error="monthError"
        :can-go-next="canGoNext"
        :owner-name="ownerName"
        @change-period="onChangePeriod"
        @retry="fetchMonth"
        @new-trip="openScreen('trip', null)"
        @new-expense="openScreen('expense', null)"
        @edit-trip="openScreen('trip', $event)"
        @edit-expense="openScreen('expense', $event)"
      ></month-view>

      <trip-form
        v-else-if="screen === 'trip'"
        :profile="profile"
        :recent="recentPlaces"
        :trip-id="editing"
        :default-date="defaultDate"
        @saved="onSaved"
        @cancel="closeScreen"
      ></trip-form>

      <expense-form
        v-else-if="screen === 'expense'"
        :profile="profile"
        :expense-id="editing"
        :default-date="defaultDate"
        @saved="onSaved"
        @cancel="closeScreen"
      ></expense-form>
    </template>
  </div>
</template>

<script>
import {nextTick} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import useToast from '@/core/util/composable/useToast';
import {
  NOTE_SPESE_API_PATH,
  inlineErrorMessage,
} from '@/orangehrmNoteSpesePlugin/util/apiError';
import MonthView from '@/orangehrmNoteSpesePlugin/components/mobile/MonthView.vue';
import TripForm from '@/orangehrmNoteSpesePlugin/components/mobile/TripForm.vue';
import ExpenseForm from '@/orangehrmNoteSpesePlugin/components/mobile/ExpenseForm.vue';

// Marcatore nello stato della cronologia: il "indietro" del telefono chiude
// il modulo e torna al mese invece di uscire dalla pagina.
const HISTORY_MARK = 'noteSpeseForm';

function periodOf(ymd) {
  const [year, month] = ymd.split('-').map((part) => parseInt(part, 10));
  return {year, month};
}

function pad(n) {
  return String(n).padStart(2, '0');
}

export default {
  name: 'MobileApp',

  components: {
    'month-view': MonthView,
    'trip-form': TripForm,
    'expense-form': ExpenseForm,
  },

  setup() {
    const toast = useToast();
    const baseUrl = window.appGlobal.baseUrl;

    const profileHttp = new APIService(baseUrl, '/api/v2/note-spese/me');
    const monthHttp = new APIService(baseUrl, '/api/v2/note-spese/month');
    const recentHttp = new APIService(
      baseUrl,
      '/api/v2/note-spese/places/recent',
    );
    [profileHttp, monthHttp, recentHttp].forEach((http) =>
      http.setIgnorePath(NOTE_SPESE_API_PATH),
    );

    return {toast, profileHttp, monthHttp, recentHttp};
  },

  data() {
    return {
      status: 'loading',
      loadError: '',
      profile: null,
      period: null,
      report: null,
      isLoadingMonth: false,
      monthError: '',
      monthRequest: 0,
      recentPlaces: [],
      screen: 'month',
      editing: null,
      monthScrollY: 0,
    };
  },

  computed: {
    ownerName() {
      if (!this.profile) return '';
      return [this.profile.firstName, this.profile.lastName]
        .filter(Boolean)
        .join(' ');
    },

    todayPeriod() {
      return periodOf(this.profile.today);
    },

    canGoNext() {
      const {year, month} = this.period;
      const today = this.todayPeriod;
      return year * 12 + month < today.year * 12 + today.month;
    },

    // Oggi se si sta guardando il mese corrente, altrimenti il primo giorno
    // del mese mostrato: chi torna indietro di un mese vuole registrare lì.
    defaultDate() {
      const {year, month} = this.period;
      const today = this.todayPeriod;
      if (year === today.year && month === today.month) {
        return this.profile.today;
      }
      return `${year}-${pad(month)}-01`;
    },
  },

  created() {
    this.fetchProfile();
  },

  mounted() {
    window.addEventListener('popstate', this.onPopState);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  },

  beforeUnmount() {
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },

  methods: {
    async fetchProfile() {
      this.status = 'loading';
      try {
        const response = await this.profileHttp.getAll();
        this.profile = response.data.data;
        this.status = 'ready';
      } catch (error) {
        this.loadError =
          inlineErrorMessage(error) ||
          'Non riesco a caricare la pagina in questo momento.';
        this.status = 'error';
        return;
      }
      if (!this.profile.isDirector) return;

      this.period = periodOf(this.profile.today);
      this.fetchMonth();
      this.fetchRecentPlaces();
    },

    async fetchMonth() {
      // Cambiando mese in fretta, una risposta lenta non deve sovrascrivere
      // quella del mese ora mostrato.
      const request = ++this.monthRequest;
      const {year, month} = this.period;
      this.isLoadingMonth = true;
      this.monthError = '';
      try {
        const response = await this.monthHttp.getAll({year, month});
        if (request !== this.monthRequest) return;
        this.report = response.data.data;
      } catch (error) {
        if (request !== this.monthRequest) return;
        this.report = null;
        this.monthError =
          inlineErrorMessage(error) || 'Non riesco a caricare questo mese.';
      } finally {
        if (request === this.monthRequest) this.isLoadingMonth = false;
      }
    },

    async fetchRecentPlaces() {
      try {
        const response = await this.recentHttp.getAll();
        this.recentPlaces = response.data.data || [];
      } catch (error) {
        // Senza recenti l'indirizzo si cerca comunque per nome.
      }
    },

    onChangePeriod(period) {
      this.period = period;
      this.report = null;
      this.fetchMonth();
    },

    openScreen(screen, id) {
      this.monthScrollY = window.scrollY;
      this.screen = screen;
      this.editing = id;
      window.history.pushState({[HISTORY_MARK]: true}, '');
      window.scrollTo(0, 0);
    },

    // Chiude il modulo passando dalla cronologia, così la voce aggiunta da
    // openScreen() non resta orfana; il ritorno al mese lo fa onPopState().
    closeScreen() {
      if (window.history.state && window.history.state[HISTORY_MARK]) {
        window.history.back();
      } else {
        this.showMonth();
      }
    },

    onPopState() {
      if (this.screen !== 'month') this.showMonth();
    },

    showMonth() {
      this.screen = 'month';
      this.editing = null;
      nextTick(() => window.scrollTo(0, this.monthScrollY));
    },

    onSaved({date, message, placesChanged}) {
      this.toast.success({title: 'Fatto', message});
      // Dopo il salvataggio si mostra il mese della voce appena salvata,
      // anche se la data è stata spostata su un altro mese.
      const saved = periodOf(date);
      if (
        saved.year !== this.period.year ||
        saved.month !== this.period.month
      ) {
        this.period = saved;
        this.report = null;
        this.monthScrollY = 0;
      }
      this.fetchMonth();
      if (placesChanged) this.fetchRecentPlaces();
      this.closeScreen();
    },

    // Riaprendo l'app dopo ore in background il mese mostrato può essere
    // vecchio (voci aggiunte dal PC, mese chiuso dall'ufficio): si ricarica.
    onVisibilityChange() {
      if (
        document.visibilityState === 'visible' &&
        this.status === 'ready' &&
        this.profile.isDirector &&
        this.screen === 'month'
      ) {
        this.fetchMonth();
      }
    },
  },
};
</script>

<style scoped>
.ns-app {
  --ns-primary: var(--oxd-primary-one-color, #ff7b1d);
  --ns-text: #1d2129;
  --ns-muted: #5a6270;
  --ns-bg: #f2f3f5;
  min-height: 100vh;
  background: var(--ns-bg);
  color: var(--ns-text);
  /* Il body di no_header.html.twig non ha font: senza, il telefono usa il
     serif di sistema. */
  font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI',
    Roboto, sans-serif;
  font-size: 16px;
  line-height: 1.4;
  -webkit-text-size-adjust: 100%;
}

.ns-app__center {
  max-width: 560px;
  margin: 0 auto;
  padding: 30vh 24px 24px;
  text-align: center;
  font-size: 17px;
}

.ns-app__denied {
  background: #ffffff;
  border-radius: 12px;
  padding: 20px;
  text-align: left;
}

.ns-app__retry {
  min-height: 48px;
  padding: 0 28px;
  border: 0;
  border-radius: 10px;
  background: var(--ns-primary);
  color: #ffffff;
  font: inherit;
  font-weight: 700;
}

.ns-app__spinner {
  display: inline-block;
  width: 32px;
  height: 32px;
  border: 3px solid #d7dae0;
  border-top-color: var(--ns-primary);
  border-radius: 50%;
  animation: ns-spin 0.8s linear infinite;
}

@keyframes ns-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
